import { Box, Stack, SvgIcon, useTheme } from "@mui/material";

import { useToast, AlertBanner, Info24Px, Weight20Px } from "@qubit/autoparts";

import { skipToken } from "@reduxjs/toolkit/query";

import { useTranslation } from "react-i18next";

import { ScanningVerification } from "~/api/scanningVerifyMethod";
import { useAppDispatch, useAppSelector } from "~/app/store";
import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import envConstants from "~/config/envConstants";
import useFlag from "~/config/flags";

import { AlertInfo } from "~/features/inventory/components/AlertInfo";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import { displayPickData } from "~/lib/helpers";
import { isFetchBaseQueryError } from "~/lib/isFetchBaseQueryError";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { setIsPickQuantityConfirmed } from "~/redux/actions";
import { selectIsPickQuantityConfirmed } from "~/redux/selectors/autostoreSelectors";
import { selectAbleQtyToPick } from "~/redux/selectors/outOfStockSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import {
  selectWorkstationAutostoreGridId,
  selectWorkstationAvailablePorts,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import { useGetFocusedPickQuery } from "~/redux/warehouse/autostorePicking.hooks";

import {
  useCaptureWeightMutation,
  useVerifyPickMutation
} from "~/redux/warehouse/pick.hooks";

import { areAllPickWeightsCaptured } from "./AutostorePicking";
import { BarcodeInput } from "./BarcodeInput";

import {
  closeWeightEntryModal,
  openWeightEntryModal,
  selectIsWeightEntryModalOpen,
  selectWeightsCapturedOnPickSoFar,
  selectWeightThresholdCheckResult
} from "./autostorePicking.slice";
import { setPickedQuantityModalStatus } from "./confirmPickQuantityModal/confirmPickQuantityModal.slice";
import { ScannedWeightChips } from "./variableWeight/ScannedWeightChips";
import { WeightEntryModal } from "./variableWeight/WeightEntryModalButton";

export default function FocusedPickInfoCard({
  onRemoveWeight,
  isRemovingWeight = false
}: {
  onRemoveWeight: (weightValue: number) => void;
  isRemovingWeight?: boolean;
}) {
  const dispatch = useAppDispatch();
  const trackedPageName = useMixpanelPageName();
  const { t } = useTranslation();
  const { palette } = useTheme();

  const [captureWeight] = useCaptureWeightMutation();

  const weightThresholdCheckResult = useAppSelector(
    selectWeightThresholdCheckResult
  );
  const weightsCapturedOnPickSoFar = useAppSelector(
    selectWeightsCapturedOnPickSoFar
  );

  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const pickQuantityConfirmationEnabled =
    fulfillmentCenter?.pickQuantityConfirmationEnabled ?? false;
  const workstationId = useAppSelector(selectWorkstationId);
  const gridId = useAppSelector(selectWorkstationAutostoreGridId);
  const isPickQuantityConfirmed = useAppSelector(selectIsPickQuantityConfirmed);
  const ableToPickQty = useAppSelector(selectAbleQtyToPick);

  // only expecting one parent port at this time since picking does not support independent multi-ports
  const [portId] = useAppSelector(selectWorkstationAvailablePorts);
  const portStatus = usePortStatusQuery(portId);
  const binIsPresent = portStatus.isReady || portStatus.isBinOpen;

  const { data: focusedPick, isLoading: isFocusedPickLoading } =
    useGetFocusedPickQuery(workstationId ? { workstationId } : skipToken);
  const { pickingNotes } = focusedPick || {};

  const [verifyPick] = useVerifyPickMutation();

  const { errorToast } = useToast();

  const { simulateUpcScan: simulateUpcScanFeatureFlag, enableColorChanges } =
    useFlag();

  const isWeightedPick = !!focusedPick?.weightThreshold;
  const pickQtyForWeights = ableToPickQty ?? focusedPick?.quantity?.value;
  const arePickWeightsReady =
    !isWeightedPick ||
    areAllPickWeightsCaptured({
      pickQty: pickQtyForWeights,
      weightsCapturedOnPickSoFar,
      weightThresholdCheckResult
    });
  const isWeightEntryModalOpen = useAppSelector(selectIsWeightEntryModalOpen);
  const weightsLeftToCapture =
    (pickQtyForWeights ?? 0) - weightsCapturedOnPickSoFar.length;
  const isOverweight = weightThresholdCheckResult === "AboveMaximum";
  const isUnderweight =
    !weightsLeftToCapture && weightThresholdCheckResult === "BelowMinimum";

  const captureWeightForPick = async ({
    productCode,
    enteredWeight
  }: {
    productCode: string;
    enteredWeight?: number;
  }): Promise<{
    isSuccess: boolean;
    weightsReady?: boolean;
    shouldClosePopover?: boolean;
  }> => {
    if (!focusedPick?.weightThreshold) return { isSuccess: false };

    try {
      const weightCaptureResult = await captureWeight({
        pickId: focusedPick.pickId,
        productCode,
        entryMethod: "Manual",
        workstationId,
        ...(ableToPickQty !== null && {
          adjustedPickQuantity: {
            value: ableToPickQty,
            units: focusedPick.quantity.units
          }
        }),
        ...(enteredWeight && {
          enteredWeight: {
            units: focusedPick.weightThreshold.maximum.units,
            value: enteredWeight
          }
        })
      }).unwrap();

      const captureWeightState = weightCaptureResult.captureWeightState;

      const weightsReady = areAllPickWeightsCaptured({
        pickQty: pickQtyForWeights,
        weightsCapturedOnPickSoFar:
          captureWeightState.allWeightsCapturedOnPickSoFar,
        weightThresholdCheckResult:
          captureWeightState.weightThresholdCheckResult
      });

      return { isSuccess: true, weightsReady };
    } catch (error) {
      const message = getMessageFromRtkError(error);
      errorToast(message);
      const weightRequired = message
        .toLowerCase()
        .includes("weight is required");
      if (isFetchBaseQueryError(error) && weightRequired)
        dispatch(openWeightEntryModal());
      return { isSuccess: false, shouldClosePopover: weightRequired };
    }
  };

  const verifyFocusedPick = async (
    upcOrSku: string,
    trackType: "Manual Input" | "Button Click"
  ): Promise<boolean> => {
    if (!focusedPick) return false;

    const pickId = focusedPick.pickId;
    const shouldSkipQuantityConfirmation =
      ableToPickQty === 1 || focusedPick.quantity?.value === 1;

    mixpanelTrack({
      trackedPageName,
      type: trackType,
      label: "Confirm Pick",
      eventProperties: {
        "Input Method":
          trackType === "Manual Input" ? "Manual" : "Product Image",
        "Pick Id": pickId,
        SKU: upcOrSku,
        Quantity: focusedPick.quantity?.value
      }
    });

    try {
      await verifyPick({
        upcOrSku,
        pickId,
        verifyMethod: ScanningVerification.INPUT,
        workstationId
      }).unwrap();

      if (shouldSkipQuantityConfirmation) {
        dispatch(setIsPickQuantityConfirmed(true));
        dispatch(
          setPickedQuantityModalStatus({
            isShown: false,
            pickedQuantity: null,
            selectedBinId: null
          })
        );

        await displayPickData({
          portId,
          pickId,
          batchId: focusedPick.batchId,
          gridId
        });
      } else {
        dispatch(
          setPickedQuantityModalStatus({
            isShown: true,
            pickedQuantity: ableToPickQty || focusedPick.quantity?.value,
            selectedBinId: focusedPick.autostoreBinId
          })
        );
      }
      return true;
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
      return false;
    }
  };

  const handleProductImageClick = async (): Promise<boolean> => {
    if (!focusedPick || isOverweight) return false;

    if (isWeightedPick && !arePickWeightsReady) {
      if (weightsLeftToCapture > 0) {
        dispatch(openWeightEntryModal());
      }
      // no weights left to capture but pick is out of threshold, don't do anything
      return false;
    }

    return verifyFocusedPick(
      focusedPick.primaryUpc || focusedPick.sku,
      "Button Click"
    );
  };

  const handleManualUpcEntry = async (enteredUpc: string): Promise<boolean> => {
    if (!focusedPick) return false;

    if (isWeightedPick && !arePickWeightsReady) {
      const result = await captureWeightForPick({ productCode: enteredUpc });

      if (!result.isSuccess) {
        return result.shouldClosePopover ?? false;
      }

      if (!result.weightsReady) return false;
    }

    return verifyFocusedPick(enteredUpc, "Manual Input");
  };

  const handleWeightEntryConfirm = async (
    enteredWeight: number
  ): Promise<boolean> => {
    if (!focusedPick || !isWeightedPick || arePickWeightsReady) return true;

    const result = await captureWeightForPick({
      productCode: focusedPick.sku,
      enteredWeight
    });
    return result.isSuccess;
  };

  const confirmationRequired =
    pickQuantityConfirmationEnabled &&
    simulateUpcScanFeatureFlag &&
    binIsPresent &&
    !isPickQuantityConfirmed;

  return (
    <Box mt={"1.25em"}>
      {!isFocusedPickLoading && focusedPick ? (
        <>
          <ProductCard.Root
            productName={focusedPick.productName}
            quantity={focusedPick.countOnHand?.value}
            imageFileName={focusedPick.imageFilename}
            onProductImageClick={handleProductImageClick}
            disabled={!confirmationRequired}
            tempZone={focusedPick.temperatureZone}
            sx={
              isUnderweight || isOverweight
                ? {
                    backgroundColor: "error.light",
                    border: `4px solid ${palette.error.dark}`
                  }
                : {}
            }
          >
            {pickQuantityConfirmationEnabled && (
              <BarcodeInput onManualUpcEntry={handleManualUpcEntry} />
            )}
            <ProductCard.Sku sku={focusedPick.sku} />
            {pickingNotes && (
              <AlertBanner
                severity={enableColorChanges ? "warning" : "info"}
                icon={
                  enableColorChanges ? (
                    <SvgIcon component={Info24Px} width={6} height={6} />
                  ) : undefined
                }
                sx={{ mt: 1 }}
              >
                {pickingNotes}
              </AlertBanner>
            )}
            <Stack direction="row" gap={0}>
              <ProductCard.ProductCount
                count={{
                  value: focusedPick.unitAmount,
                  units: focusedPick.unitFormatted
                }}
              />
              {isWeightedPick && (
                <AlertInfo
                  type="warning"
                  message={t("weight required")}
                  icon={<Weight20Px />}
                />
              )}
            </Stack>
            {envConstants.SHOW_SCANNABLE_BAR_CODES === "true" && (
              <ProductCard.QRCode upc={focusedPick.primaryUpc} />
            )}
          </ProductCard.Root>
          {isWeightedPick && (
            <ScannedWeightChips
              weightThreshold={focusedPick.weightThreshold}
              onRemoveWeight={onRemoveWeight}
              isRemovingWeight={isRemovingWeight}
            />
          )}
          {isWeightedPick && isWeightEntryModalOpen && (
            <WeightEntryModal
              open={isWeightEntryModalOpen}
              onClose={() => dispatch(closeWeightEntryModal())}
              onConfirm={handleWeightEntryConfirm}
            />
          )}
        </>
      ) : (
        <ProductCard.Loading innerHeight="32vH" />
      )}
    </Box>
  );
}
